import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { SendInformationLoaderProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { InvoiceHelper } from "utils/helpers";
//#endregion
//#region components
import useLoadSendMailOptions from "../sendMailOptions/useSendMailOptions";
import useGetMailOptionDefaults from "../sendMailOptions/useGetMailOptionDefaults";
import { useCustomerService } from "serviceHooks";
//#endregion

const SendInformationLoader: React.FC<SendInformationLoaderProps> = (props) => {
  const {
    values,
    invoiceId,
    setValues,
    connect,
    version,
    isReminder,
    nextInvoiceSerialNumber,
    skipSendInformationLoad,
  } = props;

  const customerService = useCustomerService();

  const defaultInvoiceReminderSendOption = useSelector(
    (store: AppState) => store.auth.selectedCompany.defaultInvoiceReminderSendOption
  );

  React.useEffect(() => {
    if (skipSendInformationLoad || values.mailOptions !== undefined) {
      return;
    }

    loadSendInformation();
  }, [invoiceId, version]);

  const { loadSendMailOptions } = useLoadSendMailOptions();
  const { getCreateInvoiceDefaultEmailOptions } = useGetMailOptionDefaults();

  const loadSendInformation = () => {
    const customerId = "customerId" in values ? values.customerId : (values.customer?.id as number);
    const referencePersonId =
      "referencePersonId" in values
        ? values.referencePersonId
        : "referencePerson" in values
          ? values.referencePerson?.id
          : undefined;

    if (!customerId) {
      return;
    }

    Promise.all([
      customerService.getCustomer(customerId!),
      loadSendMailOptions(invoiceId, customerId, version ?? (isReminder ? 1 : undefined)),
    ]).then(([customerResult, sendMailOptions]) => {
      let referenceEmail: string | undefined;

      if (referencePersonId !== undefined) {
        referenceEmail = customerResult.referencePersonsList?.find(
          (refPerson) => refPerson.id === referencePersonId
        )?.emailAddress;
      }

      const mailOptions: InvoiceFormTypes.SendMailOptions = {
        ...sendMailOptions,
        defaultEmailTo: { customer: customerResult.emailAddress, referencePerson: referenceEmail },
      };

      let defaultEmailToSettings: InvoiceFormTypes.DefaultEmailToSettings = {
        emailToType: customerResult.invoiceDeliverySettings?.invoiceMailToType,
        customEmail: customerResult.invoiceDeliverySettings?.customInvoiceMailTo,
      };

      let sendType = values.sendType;

      if (isReminder) {
        sendType =
          customerResult.invoiceReminderSettings?.invoiceReminderSendOption ??
          defaultInvoiceReminderSendOption ??
          sendType;

        defaultEmailToSettings = {
          emailToType: customerResult.invoiceReminderSettings?.invoiceReminderMailToType,
          customEmail: customerResult.invoiceReminderSettings?.customInvoiceReminderMailTo,
        };
      }

      let sendMail = values.sendMail;
      if (
        !values.sendMail ||
        (isReminder &&
          customerResult.invoiceReminderSettings?.invoiceReminderSendOption ===
          WintSuperkollAPIModel.InvoiceSendMethod.Email)
      ) {
        sendMail = getCreateInvoiceDefaultEmailOptions(mailOptions, defaultEmailToSettings, {});
      } else {
        if (sendMail?.emailMessage === undefined) {
          sendMail!.emailMessage = mailOptions.mailMessage;
        }

        if (sendMail?.emailSubject === undefined) {
          sendMail!.emailSubject = mailOptions.mailSubject;
        }

        const mailToList = sendMail?.emailTo?.map((item) => item.value) ?? [];
        mailToList.forEach((mailTo) => {
          if (!mailOptions.mailToList.some((item) => item.value === mailTo)) {
            mailOptions.mailToList.push({ id: mailTo, value: mailTo });
          }
        });
      }

      setValues((prevState: any) => ({
        ...prevState,
        sendType: isReminder ? sendType : prevState.sendType,
        mailOptions: mailOptions,
        sendMail: sendMail,
        // Kept on the form so anything that later recomputes the default recipient (e.g. saving a
        // reference person from "Skicka som") resolves it against the customer's own send settings
        // rather than guessing.
        defaultEmailToSettings: defaultEmailToSettings,
        loadingMailOptions: false,
        loadingSendType: false,
      }));
    });
  };

  React.useEffect(() => {
    if (!nextInvoiceSerialNumber || !values.sendMail?.emailSubject) {
      return;
    }
    const updatedSubject = InvoiceHelper.fillSerialNumberTemplate(
      values.sendMail.emailSubject,
      nextInvoiceSerialNumber
    );
    if (values.sendMail.emailSubject !== updatedSubject) {
      const emailSubjectConnect = connect("sendMail.emailSubject");
      emailSubjectConnect.setValue(updatedSubject);
    }
  }, [nextInvoiceSerialNumber, values.sendMail?.emailSubject]);

  return <></>;
};

export default SendInformationLoader;
